import { Link } from 'react-router-dom';
import { Typography, Divider } from 'antd';
import Footer from '../../Routes/Footer';
import PublicHeader from '../../Routes/PublicHeader';
import { useNodeInfo } from '../../hooks/use_node_info';
import { useNodeSettings } from '../../hooks/use_node_settings';

const { Title, Paragraph, Text } = Typography;

const LAST_UPDATED = '2026-09-22';

function TermsOfUse() {
    const { featureTrustPage } = useNodeInfo();
    const { legalEntityName, platformLegalEntityName } = useNodeSettings();
    return (
      <>
        <PublicHeader />
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 16px 64px' }}>
            <Title level={3}>Terms of Use</Title>
            <Paragraph type="secondary">Last updated: {LAST_UPDATED}</Paragraph>

            <Paragraph>
                These terms are between you and {legalEntityName || platformLegalEntityName}
                {' '}("we", "us"), the operator of this node.
                {legalEntityName && <> {platformLegalEntityName} is not a party to
                  these terms — it builds and runs the underlying Vellby platform
                  (the software, hosting, and AI features every node runs on) as
                  {' '}{legalEntityName}'s service provider, under {legalEntityName}'s
                  instructions. See the <Link to="/privacy">Privacy Policy</Link> for
                  exactly how that split works.</>} By creating an account or using
                Vellby, you agree to these terms. See also our <Link to="/privacy">Privacy Policy</Link>
                {featureTrustPage && <> and <Link to="/trust">Trust page</Link></>} for how
                we handle data.
            </Paragraph>

            <Title level={4}>The service</Title>
            <Paragraph>
                Vellby lets you organize contexts, people, relationships and apps as
                entities; connect them; message and hold hangouts with the people
                you're connected to; and connect your own AI agent, through an agent
                token you create, to search and act on your own data. Access is invite-based or through a self-serve signup on a
                given node, authenticated through Keycloak.
            </Paragraph>

            <Title level={4}>Your account</Title>
            <ul>
                <li>You're responsible for the activity on your account and for keeping your credentials secure.</li>
                <li>You can delete your account at any time from Account settings — this is self-service and permanently removes your login and the data described in our <Link to="/privacy">Privacy Policy</Link>.</li>
                <li>We may suspend or terminate accounts that violate these terms or misuse the service.</li>
            </ul>

            <Title level={4}>Acceptable use</Title>
            <Paragraph>
                Don't use Vellby to violate the law, harass others, upload content you
                don't have the right to share, or attempt to abuse, overload, or
                misuse an agent token (for example, sharing it with someone else, or
                using it to scrape other people's data or attack other systems). We may add a more detailed Acceptable Use Policy over time —
                this section will be updated to link to it once published.
            </Paragraph>

            <Title level={4}>Your content</Title>
            <Paragraph>
                You own the entities, messages, and other content you create in
                Vellby. You grant us the limited right to store, process, and transmit
                it as needed to provide the service — including generating AI
                embeddings from it (see the{' '}
                <Link to="/privacy">Privacy Policy</Link> for which third parties
                process this). If your node connects with another Vellby server
                (federation), content relevant to that specific interaction is shared
                with that server as part of normal operation.
            </Paragraph>

            <Title level={4}>AI features</Title>
            <Paragraph>
                Search embeddings use a third-party AI model (currently Google
                Gemini). We're moving this
                processing in-house, to language models we host ourselves — see the{' '}
                <Link to="/privacy">Privacy Policy</Link> for what that means for your
                data. Vellby has no built-in chat agent: if you connect your own, it
                acts as you, within the scope of its token, and you're responsible
                for what it does. Like any AI system, it can make mistakes; don't rely
                on it for decisions where accuracy is critical without independent
                verification.
            </Paragraph>

            <Title level={4}>Billing</Title>
            <Paragraph>
                Paid plans are billed as described at checkout or in your order form.
                Payment processing is handled by Stripe; we don't store your card
                details. Refund and cancellation terms will be published separately
                as our Refund/Billing Terms.
            </Paragraph>

            <Title level={4}>Availability</Title>
            <Paragraph>
                We aim to keep Vellby available and reliable but don't guarantee
                uninterrupted service, except where a separate Service Level
                Agreement has been agreed for your organization.
            </Paragraph>

            <Title level={4}>Disclaimer &amp; liability</Title>
            <Paragraph>
                Vellby is provided "as is," without warranties of any kind. To the
                extent permitted by law, neither {legalEntityName || platformLegalEntityName}
                {legalEntityName && <> nor {platformLegalEntityName}, in its role as
                  platform provider,</>} will be liable for indirect, incidental, or
                consequential damages arising from your use of the service.
            </Paragraph>

            <Title level={4}>Changes to these terms</Title>
            <Paragraph>
                We'll update the date at the top of this page whenever these terms
                change, and where required, notify you directly.
            </Paragraph>

            <Divider />
            <Paragraph type="secondary">
                Questions about these terms? Reach us at <Text code>info@vellby.com</Text>.
            </Paragraph>
            <Footer />
        </div>
      </>
    );
}

export default TermsOfUse;
